<script setup lang="ts">
import { copyToClipboard } from '@@/bits/clipboard'
import type { AutoplayDurationType, EmbedSizeOption } from '@@/bits/slideshow'
import { embedSizeOptions } from '@@/bits/slideshow'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzDivider, { OzDividerColorScheme } from '@@/library/v4/components/OzDivider.vue'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSlideshowStore } from '@@/pinia/slideshow'
import { useSurfaceStore } from '@@/pinia/surface'
import SurfaceSlideshowRowDropdownAutoplayDuration from '@@/vuecomponents/SurfaceSlideshowRowDropdownAutoplayDuration.vue'
import SurfaceSlideshowRowDropdownEmbedSize from '@@/vuecomponents/SurfaceSlideshowRowDropdownEmbedSize.vue'
import { useSlideshowPanels } from '@@/vuecomposables/slideshow_panels'
import { useSlideshowStyles } from '@@/vuecomposables/slideshow_styles'
import { __ } from '@@/bits/intl'
import OzIconButton, {
  OzIconButtonColorScheme,
  OzIconButtonSizePreset,
} from '@@/library/v4/components/OzIconButton.vue'
import OzPlainButton, { OzPlainButtonColorScheme } from '@@/library/v4/components/OzPlainButton.vue'
import OzRowToggle from '@@/library/v4/components/OzRowToggle.vue'
import OzScreenSizeAwareSideModal from '@@/library/v4/components/OzScreenSizeAwareSideModal.vue'
import { storeToRefs } from 'pinia'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { computed, ref } from 'vue'
import SurfacePanelHeader from '@@/vuecomponents/SurfacePanelHeader.vue'

const globalSnackbarStore = useGlobalSnackbarStore()
const { links } = storeToRefs(useSurfaceStore())
const { slideshowEmbedCodeUrlTemplate } = storeToRefs(useSlideshowStore())
const { isEmbedPanelOpen } = useSlideshowPanels()
const { hasDarkBackgroundLuminance } = useSlideshowStyles()
const { isSmallerThanTabletPortrait, isSmallerThanDesktop } = storeToRefs(useWindowSizeStore())

/*
 * Embed code
 */

const embedStyle = computed(() => `width:${embedSize.value.width}px;height:${embedSize.value.height}px;`)
const embedCode = computed(() => {
  const defaultParams = new URLSearchParams({
    autoplay: isEmbedAutoplaying.value ? '1' : '0',
    loop: isEmbedLooping.value ? '1' : '0',
    duration: `${embedAutoplayDuration.value}`,
  })
  const embedUrl = `${links.value.embed}/slideshow?${defaultParams.toString()}`
  return slideshowEmbedCodeUrlTemplate.value.replace('{embedUrl}', embedUrl).replace('{embedStyle}', embedStyle.value)
})

const copyEmbedCode = async (): Promise<void> => {
  const result = await copyToClipboard(embedCode.value)
  if (result) {
    globalSnackbarStore.setSnackbar({ message: __('Copied to your clipboard.') })
  } else {
    globalSnackbarStore.setSnackbar({ message: __('Could not copy to your clipboard.') })
  }
}

/*
 * Embed size settings
 */
const embedSize = ref(embedSizeOptions[1])
const onEmbedSizeSelect = (size: EmbedSizeOption) => {
  embedSize.value = size
}

/*
 * Autoplay settings
 */
const isEmbedAutoplaying = ref(false)
const embedAutoplayDuration = ref<AutoplayDurationType>('auto')
const isEmbedLooping = ref(false)

const onToggleAutoplay = () => {
  if (isEmbedAutoplaying.value) {
    isEmbedAutoplaying.value = false
  } else {
    isEmbedAutoplaying.value = true
  }
}
const onAutoplayDurationSelect = (duration: AutoplayDurationType) => {
  embedAutoplayDuration.value = duration
}
</script>

<template>
  <OzScreenSizeAwareSideModal
    class="font-sans"
    aria-labelledby="slideshow-embed-panel-title"
    :full-size="true"
    :is-phone="isSmallerThanTabletPortrait"
    :width-class="isSmallerThanDesktop ? 'w-100' : 'w-120'"
    :dark-mode="hasDarkBackgroundLuminance"
    :z-index-class="'z-sidepanel'"
    :scrollbar-overlay="true"
    :top-inner-corner-radius="0"
    @scrim-click="isEmbedPanelOpen = false"
    @scrim-esc="isEmbedPanelOpen = false"
  >
    <template #header>
      <SurfacePanelHeader class="pb-3" :fixed-width="false">
        <template #start-content>
          <OzIconButton
            icon-name="chevron_left_center"
            :size-preset="OzIconButtonSizePreset.H32px"
            :label="__('Close slideshow embed panel')"
            :color-scheme="OzIconButtonColorScheme.SecondaryClear"
            :dark-mode="hasDarkBackgroundLuminance"
            @click="isEmbedPanelOpen = false"
          />
        </template>
        <template #center-content>
          <h2
            id="slideshow-embed-panel-title"
            :class="[
              'text-heading-3 text-center font-semibold',
              hasDarkBackgroundLuminance ? 'text-light-text-100' : 'text-dark-text-100',
              'w-full',
            ]"
          >
            {{ __('Embed this slideshow') }}
          </h2>
        </template>
      </SurfacePanelHeader>
    </template>
    <template #body>
      <div :class="'px-4 pt-3 pb-5'">
        <p :class="['ps-2 pb-2', hasDarkBackgroundLuminance ? 'text-light-text-200' : 'text-dark-text-200']">
          {{ __('Settings') }}
        </p>
        <OzBox :radius="20" :color="OzBoxColors.Secondary" :dark-mode="hasDarkBackgroundLuminance" class="px-4 py-1">
          <SurfaceSlideshowRowDropdownEmbedSize
            class="ps-1 box-border"
            :embed-size="embedSize"
            :dark-mode="hasDarkBackgroundLuminance"
            @select-row="onEmbedSizeSelect"
          />
          <OzDivider :dark-mode="hasDarkBackgroundLuminance" :color-scheme="OzDividerColorScheme.Solid" />
          <OzRowToggle
            :value="isEmbedAutoplaying"
            :label="__('Autoplay slides')"
            class="w-full py-4 pe-0 ps-1"
            :dark-mode="hasDarkBackgroundLuminance"
            @click="onToggleAutoplay"
          >
            <p :class="['text-body', hasDarkBackgroundLuminance ? 'text-light-text-100' : 'text-dark-text-100']">
              {{ __('Autoplay') }}
            </p>
          </OzRowToggle>
          <template v-if="isEmbedAutoplaying">
            <OzDivider :dark-mode="hasDarkBackgroundLuminance" :color-scheme="OzDividerColorScheme.Solid" />
            <SurfaceSlideshowRowDropdownAutoplayDuration
              class="ps-1 box-border"
              :embed-autoplay-duration="embedAutoplayDuration"
              :dark-mode="hasDarkBackgroundLuminance"
              @select-row="onAutoplayDurationSelect"
            />
            <OzDivider :dark-mode="hasDarkBackgroundLuminance" :color-scheme="OzDividerColorScheme.Solid" />
            <OzRowToggle
              :value="isEmbedLooping"
              :label="__('Loop slides')"
              class="w-full py-4 pe-0 ps-1"
              :dark-mode="hasDarkBackgroundLuminance"
              @click="isEmbedLooping = !isEmbedLooping"
            >
              <p :class="['text-body', hasDarkBackgroundLuminance ? 'text-light-text-100' : 'text-dark-text-100']">
                {{ __('Loop slides') }}
              </p>
            </OzRowToggle>
          </template>
        </OzBox>
      </div>

      <div :class="'px-4 pt-3 pb-8'">
        <p :class="['ps-2 pb-2', hasDarkBackgroundLuminance ? 'text-light-text-200' : 'text-dark-text-200']">
          {{ __('Code') }}
        </p>
        <OzBox :radius="20" :color="OzBoxColors.Secondary" :dark-mode="hasDarkBackgroundLuminance" class="py-1 px-5">
          <div class="py-3 px-1 w-full box-border">
            <code class="break-words block text-[10px] leading-[14px] font-mono" data-testid="slideshowEmbedCode">{{
              embedCode
            }}</code>
          </div>
          <OzDivider :dark-mode="hasDarkBackgroundLuminance" :color-scheme="OzDividerColorScheme.Solid" />
          <div class="flex justify-end py-3 pe-3">
            <OzPlainButton
              :aria-label="__('Copy slideshow embed code')"
              :text="__('Copy slideshow embed code')"
              :class="['px-0']"
              :color-scheme="OzPlainButtonColorScheme.Primary"
              :dark-mode="hasDarkBackgroundLuminance"
              @click="copyEmbedCode"
            />
          </div>
        </OzBox>
      </div>
    </template>
  </OzScreenSizeAwareSideModal>
</template>
